import { toRaw } from "vue"
import { ElMessage } from "element-plus"
import router from "@/router"
import { bimKatselinUrl } from "@/bim-http"
import { BimCompliance } from "@/types/bim"

/** Mallikatselin omassa ikkunassaan.
 *
 *  Tama on **moduulisingleton, ei composable**: ikkuna avataan hankenakymasta
 *  ja sita kaytetaan huonekorttinakymasta, jotka ovat eri reitteja. Komponentin
 *  elinkaari ei siis voi omistaa ikkunaviitetta — se katoaisi heti kun
 *  hankenakyma puretaan.
 *
 *  Vahti omistaa datan; katselin on pelkka renderoija joka ei kutsu Vahdin
 *  API:a eika kasittele Auth0-tokeneita (arkkitehtuuriperiaate 1). Vertailu
 *  lasketaan Vahdin puolella ja tyonnetaan tanne postMessagella.
 */

/** Viestin tunniste. Sivulla on muitakin postMessage-lahettajia (webpack
 *  dev-server, selainlaajennukset), joten vieraat viestit on siivilöitävä. */
const LAHDE = "vahti-bim"

/** Ikkunan nimi. Sama nimi joka kerta, jotta samaan hankkeeseen ei kerry
 *  useaa mallikatselinta. */
const IKKUNA = "vahti-bim-malli"

const MITAT = "width=1400,height=900,menubar=no,toolbar=no,location=no"

type Katselimelta =
    | { lahde: typeof LAHDE; tyyppi: "valmis" }
    | {
          lahde: typeof LAHDE
          tyyppi: "tilaValittu"
          tilatunnus: string | null
          huonekorttiId: number | null
          status: string | null
      }
    | { lahde: typeof LAHDE; tyyppi: "avaaHuonekortti"; huonekorttiId: number }

/** Katselimen origin. Devissa erillinen Vite-palvelin (:5173), demossa sama
 *  origin kuin Vahdilla (/bim/). */
const katselinOrigin = new URL(bimKatselinUrl, location.href).origin

/** `embed=1` kertoo katselimelle etta data tulee viestina eika fetchilla,
 *  `host` kertoo kenelle se saa vastata. Host annetaan nimenomaisesti eika
 *  paatella, jotta mika tahansa sivu ei voi avata katselinta ja lukea sen
 *  viesteja. */
const katselinUrl = () => {
    const url = new URL(bimKatselinUrl, location.href)
    url.searchParams.set("embed", "1")
    url.searchParams.set("host", location.origin)

    return url.toString()
}

/* ------------------------------------------------------------------- tila */

let ikkuna: Window | null = null
/** Onko katselin ilmoittanut olevansa valmis vastaanottamaan dataa. */
let valmis = false
let viimeisinData: BimCompliance | null = null
/** Tilatunnus jota pyydettiin ennen kuin katselin oli valmis. */
let jonossaTilatunnus: string | null = null

/** Vue-komponentit voivat seurata tata nappitekstin vaihtamiseen. Ei ref,
 *  koska `closed` voi muuttua ilman etta Vahti saa siita tapahtumaa —
 *  arvo on siksi luettava kutsuhetkella. */
export const auki = () => !!ikkuna && !ikkuna.closed

/* --------------------------------------------------------------- lahetys */

const laheta = (viesti: Record<string, unknown>) => {
    if (!auki()) return
    ikkuna!.postMessage({ lahde: LAHDE, ...viesti }, katselinOrigin)
}

const lahetaData = () => {
    if (!viimeisinData || !valmis) return
    // toRaw on PAKOLLINEN: vue-query palauttaa datan readonly(reactive(...))
    // -proxyna, eika structured clone osaa kloonata Proxya ->
    // "DOMException: Proxy object could not be cloned". Virhe kaataa koko
    // lahetyksen, joten katselin jaisi odottamaan dataa ikuisesti.
    laheta({ tyyppi: "data", compliance: toRaw(viimeisinData) })
}

/* ------------------------------------------------------------ vastaanotto */

/** Yksi kuuntelija koko sovelluksen elinajaksi. Komponenttikohtainen
 *  kuuntelija katoaisi kun hankenakyma puretaan, jolloin huonekortista
 *  avattu ikkuna ei enaa saisi vastausta. */
window.addEventListener("message", (e: MessageEvent) => {
    // Lahde tarkistetaan kahdesti: origin yksin ei riita, koska samasta
    // originista tulee myos muita viesteja (webpack dev-server).
    if (e.origin !== katselinOrigin || e.source !== ikkuna) return
    const v = e.data as Katselimelta | undefined
    if (!v || v.lahde !== LAHDE) return

    if (v.tyyppi === "valmis") {
        valmis = true
        lahetaData()
        if (jonossaTilatunnus) {
            laheta({ tyyppi: "valitseTila", tilatunnus: jonossaTilatunnus })
            jonossaTilatunnus = null
        }
    } else if (v.tyyppi === "avaaHuonekortti") {
        router.push({
            name: "Huonekortti",
            params: { huonekorttiId: v.huonekorttiId },
        })
    }
    // "tilaValittu" on toistaiseksi vain tiedoksi; Vahdin nakymat eivat seuraa
    // katselimen valintaa. Viesti on protokollassa jotta seuranta voidaan
    // lisata koskematta katselimeen.
})

/* ----------------------------------------------------------------- avaus */

/**
 * Avaa katselimen tai nostaa jo avatun esiin.
 *
 * **On kutsuttava synkronisesti klikkauskasittelijassa.** Jos tata edeltaa
 * `await`, selain tulkitsee avauksen ei-kayttajalahtoiseksi ja estaa sen.
 *
 * Palauttaa false jos ponnahdusikkuna estettiin.
 */
export const avaa = (): boolean => {
    if (auki()) {
        // Jo auki olevaa ikkunaa EI navigoida uudelleen: sama URL tarkoittaisi
        // 6,4 MB IFC:n uudelleenjasennysta joka kerta kun "Nayta mallissa"
        // painetaan.
        ikkuna!.focus()

        return true
    }

    // Ikkuna latautuu tyhjasta, joten valmius on nollattava. Ilman tata
    // lahettaisimme dataa ikkunaan jota ei viela ole.
    valmis = false
    ikkuna = window.open(katselinUrl(), IKKUNA, MITAT)

    if (!ikkuna) {
        ElMessage.warning(
            "Selain esti mallin avaamisen omaan ikkunaan. Salli ponnahdusikkunat tälle sivustolle."
        )

        return false
    }

    return true
}

/** Uusin vertailutulos. Talletetaan aina, lahetetaan kun katselin on valmis —
 *  data voi valmistua ennen ikkunaa tai sen jalkeen. */
export const asetaData = (c: BimCompliance | undefined) => {
    if (!c) return
    viimeisinData = c
    lahetaData()
}

/** Syvalinkki huonekortista: tilatunnus, ei GlobalId. Vahti ei tunne IFC:n
 *  tunnuksia eika sen kuulu tuntea. */
export const valitseTila = (tilatunnus: string) => {
    if (valmis && auki()) {
        laheta({ tyyppi: "valitseTila", tilatunnus })
    } else {
        jonossaTilatunnus = tilatunnus
    }
}
